import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useEditMode } from "@/contexts/EditModeContext";
import { supabase } from "@/integrations/supabase/client";
import { Plus, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

interface EditableCategoriesProps {
  value: string[];
  onSave: (categories: string[]) => void;
  disabled?: boolean;
}

export const EditableCategories = ({ value, onSave, disabled }: EditableCategoriesProps) => {
  const { isEditMode } = useEditMode();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(value);

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data } = await supabase
        .from('categories')
        .select('slug, name')
        .order('sort_order');
      return data || [];
    },
  });

  const handleToggleCategory = (categorySlug: string) => {
    setSelectedCategories(prev =>
      prev.includes(categorySlug)
        ? prev.filter(c => c !== categorySlug)
        : [...prev, categorySlug]
    );
  };

  const handleSave = () => {
    onSave(selectedCategories);
    setIsOpen(false);
  };

  const getCategoryName = (slug: string) => {
    return categories.find(c => c.slug === slug)?.name || slug;
  };

  if (!isEditMode || disabled) {
    return null; // Hide from regular users, only show in edit mode
  }

  return (
    <div className="flex flex-wrap gap-2 items-center">
      {selectedCategories.map((categorySlug) => (
        <Badge key={categorySlug} variant="outline" className="gap-1">
          {getCategoryName(categorySlug)}
          <X
            className="h-3 w-3 cursor-pointer hover:text-destructive"
            onClick={() => {
              const newCategories = selectedCategories.filter(c => c !== categorySlug);
              setSelectedCategories(newCategories);
              onSave(newCategories);
            }}
          />
        </Badge>
      ))}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-6">
            <Plus className="h-3 w-3 mr-1" />
            Add Category
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-4 bg-card z-50">
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Select Categories</h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {categories.map((category) => (
                <div key={category.slug} className="flex items-center space-x-2">
                  <Checkbox
                    id={`cat-${category.slug}`}
                    checked={selectedCategories.includes(category.slug)}
                    onCheckedChange={() => handleToggleCategory(category.slug)}
                  />
                  <Label
                    htmlFor={`cat-${category.slug}`}
                    className="text-sm cursor-pointer"
                  >
                    {category.name}
                  </Label>
                </div>
              ))}
            </div>
            <Button onClick={handleSave} size="sm" className="w-full">
              Save Categories
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
